import { Injectable } from '@angular/core';
import { ObservationReferenceRangeTemplate } from './observation-reference-range.service';
import { Coding, CodeableConcept } from 'fhir/r4';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { firstValueFrom } from 'rxjs';
import { FormFieldsSelectDataService } from '../shared/form-fields-select-data.service';

@Injectable({ providedIn: 'root' })
export class ReferenceRangeImportExportService {
  constructor(private readonly selectDataService: FormFieldsSelectDataService) {}

  private readonly unitSystem = 'http://unitsofmeasure.org';

  private readonly expectedHeaders = [
    'observationDisplay',
    'rangeType',
    'interpretation',
    'lowValue',
    'lowUnit',
    'highValue',
    'highUnit',
    'appliesToGender',
    'ageLowValue',
    'ageLowUnit',
    'ageHighValue',
    'ageHighUnit',
    'text'
  ];

  private readonly rangeTypeMap: Record<string, string> = {
    'Normal range': 'normal',
    'Critical / Needs Treatment': 'treatment',
    'Recommended range': 'recommended',
    'Therapeutic desired level': 'therapeutic'
  };

  private readonly interpretationMap: Record<string, string> = {
    'Normal (N)': 'N',
    'High (H)': 'H',
    'Low (L)': 'L',
    'Critical High (HH)': 'HH',
    'Critical Low (LL)': 'LL',
    'Abnormal (A)': 'A',
    'Critical Abnormal (AA)': 'AA',
    'Positive (POS)': 'POS',
    'Negative (NEG)': 'NEG',
    'Indeterminate (IND)': 'IND',
    'Reactive (RR)': 'RR',
    'Non-reactive (NR)': 'NR',
    'Susceptible (S)': 'S',
    'Resistant (R)': 'R',
    'Intermediate (I)': 'I'
  };

  private readonly genderMap: Record<string, string> = {
    'All': 'all',
    'Male': 'male',
    'Female': 'female',
    'Other': 'other',
    'Unknown': 'unknown'
  };

  // Reverse mapping for export
  private reverseMap(map: Record<string, string>, value: string): string {
    const entry = Object.entries(map).find(([_, val]) => val === value);
    return entry ? entry[0] : value;
  }

  async downloadExcelTemplate(): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    
    // Add hidden reference sheet for data validation
    const refSheet = workbook.addWorksheet('ReferenceData');
    refSheet.state = 'hidden';
    
    const rangeTypes = Object.keys(this.rangeTypeMap);
    const interpretations = Object.keys(this.interpretationMap);
    const genders = Object.keys(this.genderMap);
    
    let localDisplays: string[] = [];
    try {
      const codes = await firstValueFrom(this.selectDataService.getFormFieldSelectData('observation', 'localCode'));
      localDisplays = (codes || []).map((c: any) => c.display || c.code || '').filter(Boolean);
    } catch (e) {
      console.warn('Failed to load local codes for excel template', e);
    }
    
    refSheet.getColumn(1).values = ['RangeTypes', ...rangeTypes];
    refSheet.getColumn(2).values = ['Interpretations', ...interpretations];
    refSheet.getColumn(3).values = ['Genders', ...genders];
    refSheet.getColumn(4).values = ['ObservationDisplays', ...localDisplays];

    // Add main data sheet
    const dataSheet = workbook.addWorksheet('Reference Ranges');
    dataSheet.addRow(this.expectedHeaders);

    const exampleRow = [
      'Heart rate',
      'Normal range',
      'Normal (N)',
      '60',
      '/min',
      '100',
      '/min',
      'All',
      '',
      '',
      '',
      '',
      'Normal heart rate range for healthy adults'
    ];
    dataSheet.addRow(exampleRow);

    // Apply data validation to columns starting from row 2 up to row 1000
    for (let i = 2; i <= 1000; i++) {
      // rangeType column (B, which is 2)
      dataSheet.getCell(`B${i}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`ReferenceData!$A$2:$A$${rangeTypes.length + 1}`]
      };
      
      // interpretation column (C, which is 3)
      dataSheet.getCell(`C${i}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`ReferenceData!$B$2:$B$${interpretations.length + 1}`]
      };
      
      // appliesToGender column (H, which is 8)
      dataSheet.getCell(`H${i}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`ReferenceData!$C$2:$C$${genders.length + 1}`]
      };

      // observationDisplay column (A, which is 1)
      if (localDisplays.length > 0) {
        dataSheet.getCell(`A${i}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          showErrorMessage: false,
          error: 'Please enter a valid display or select from the list',
          formulae: [`ReferenceData!$D$2:$D$${localDisplays.length + 1}`]
        };
      }
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `reference-range-template-${new Date().toISOString().split('T')[0]}.xlsx`);
  }

  async downloadCommonExcelTemplate(): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const refSheet = workbook.addWorksheet('ReferenceData');
    refSheet.state = 'hidden';
    
    const rangeTypes = Object.keys(this.rangeTypeMap);
    const interpretations = Object.keys(this.interpretationMap);
    const genders = Object.keys(this.genderMap);
    
    let localCodes: any[] = [];
    let localDisplays: string[] = [];
    try {
      localCodes = await firstValueFrom(this.selectDataService.getFormFieldSelectData('observation', 'localCode')) || [];
      localDisplays = localCodes.map((c: any) => c.display || c.code || '').filter(Boolean);
    } catch (e) {
      console.warn('Failed to load local codes for common excel template', e);
    }
    refSheet.getColumn(1).values = ['RangeTypes', ...rangeTypes];
    refSheet.getColumn(2).values = ['Interpretations', ...interpretations];
    refSheet.getColumn(3).values = ['Genders', ...genders];
    refSheet.getColumn(4).values = ['ObservationDisplays', ...localDisplays];

    const dataSheet = workbook.addWorksheet('Reference Ranges');
    dataSheet.addRow(this.expectedHeaders);

    const commonObservations = [
      { keywords: ['heart rate', 'pulse'], type: 'Normal range', interp: 'Normal (N)', low: '60', lUnit: '/min', high: '100', hUnit: '/min', gender: 'All', text: 'Normal heart rate range for healthy adults' },
      { keywords: ['respiratory rate', 'breath rate'], type: 'Normal range', interp: 'Normal (N)', low: '12', lUnit: '/min', high: '20', hUnit: '/min', gender: 'All', text: 'Normal resting respiratory rate' },
      { keywords: ['systolic blood pressure', 'systolic bp'], type: 'Normal range', interp: 'Normal (N)', low: '90', lUnit: 'mm[Hg]', high: '120', hUnit: 'mm[Hg]', gender: 'All', text: 'Normal systolic blood pressure' },
      { keywords: ['diastolic blood pressure', 'diastolic bp'], type: 'Normal range', interp: 'Normal (N)', low: '60', lUnit: 'mm[Hg]', high: '80', hUnit: 'mm[Hg]', gender: 'All', text: 'Normal diastolic blood pressure' },
      { keywords: ['temperature', 'body temperature'], type: 'Normal range', interp: 'Normal (N)', low: '36.1', lUnit: 'Cel', high: '37.2', hUnit: 'Cel', gender: 'All', text: 'Normal body temperature' },
      { keywords: ['oxygen saturation', 'spo2', 'o2 saturation'], type: 'Normal range', interp: 'Normal (N)', low: '95', lUnit: '%', high: '100', hUnit: '%', gender: 'All', text: 'Normal oxygen saturation' },
      { keywords: ['leukocytes', 'white blood cells', 'wbc'], type: 'Normal range', interp: 'Normal (N)', low: '4.5', lUnit: '10*3/uL', high: '11.0', hUnit: '10*3/uL', gender: 'All', text: 'Normal WBC count' },
      { keywords: ['platelets', 'plt'], type: 'Normal range', interp: 'Normal (N)', low: '150', lUnit: '10*3/uL', high: '450', hUnit: '10*3/uL', gender: 'All', text: 'Normal platelet count' },
      { keywords: ['glucose', 'fasting blood glucose', 'fbs'], type: 'Normal range', interp: 'Normal (N)', low: '70', lUnit: 'mg/dL', high: '99', hUnit: 'mg/dL', gender: 'All', text: 'Normal fasting glucose' },
      { keywords: ['potassium'], type: 'Normal range', interp: 'Normal (N)', low: '3.6', lUnit: 'mmol/L', high: '5.2', hUnit: 'mmol/L', gender: 'All', text: 'Normal potassium' },
      { keywords: ['sodium'], type: 'Normal range', interp: 'Normal (N)', low: '135', lUnit: 'mmol/L', high: '145', hUnit: 'mmol/L', gender: 'All', text: 'Normal sodium' },
      { keywords: ['hemoglobin', 'hgb'], type: 'Normal range', interp: 'Normal (N)', low: '13.8', lUnit: 'g/dL', high: '17.2', hUnit: 'g/dL', gender: 'Male', text: 'Normal male hemoglobin' },
      { keywords: ['hemoglobin', 'hgb'], type: 'Normal range', interp: 'Normal (N)', low: '12.1', lUnit: 'g/dL', high: '15.1', hUnit: 'g/dL', gender: 'Female', text: 'Normal female hemoglobin' },
    ];

    const processedDisplays = new Set<string>();
    // Fuzzy match existing local codes against common list
    for (const codeObj of localCodes) {
      const display = codeObj.display || codeObj.code || '';
      if (!display || processedDisplays.has(display.toLowerCase())) continue;
      
      const displayLower = display.toLowerCase();
      // Find the first matching common template
      const matches = commonObservations.filter(obs => obs.keywords.some(kw => displayLower.includes(kw)));
     
      for (const match of matches) {
        dataSheet.addRow([
          display, match.type, match.interp, match.low, match.lUnit, match.high, match.hUnit, match.gender, '', '', '', '', match.text
        ]);
      }
      if (matches.length > 0) {
        processedDisplays.add(displayLower);
      }
    }

    // Apply data validation after adding rows
    for (let i = 2; i <= 1000; i++) {
      dataSheet.getCell(`B${i}`).dataValidation = { type: 'list', allowBlank: true, formulae: [`ReferenceData!$A$2:$A$${rangeTypes.length + 1}`] };
      dataSheet.getCell(`C${i}`).dataValidation = { type: 'list', allowBlank: true, formulae: [`ReferenceData!$B$2:$B$${interpretations.length + 1}`] };
      dataSheet.getCell(`H${i}`).dataValidation = { type: 'list', allowBlank: true, formulae: [`ReferenceData!$C$2:$C$${genders.length + 1}`] };
      if (localDisplays.length > 0) {
        dataSheet.getCell(`A${i}`).dataValidation = { type: 'list', allowBlank: true, showErrorMessage: false, error: 'Please enter a valid display or select from the list', formulae: [`ReferenceData!$D$2:$D$${localDisplays.length + 1}`] };
      }
    }
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `common-reference-ranges-${new Date().toISOString().split('T')[0]}.xlsx`);
  }

  async parseExcel(file: File): Promise<{ templates: ObservationReferenceRangeTemplate[]; errors: string[] }> {
    const errors: string[] = [];
    const templates: ObservationReferenceRangeTemplate[] = [];

    try {
      let localCodes: any[] = [];
      try {
        localCodes = await firstValueFrom(this.selectDataService.getFormFieldSelectData('observation', 'localCode')) || [];
      } catch (e) {
        console.warn('Failed to fetch local codes during excel parse', e);
      }

      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(await file.arrayBuffer());
      
      const sheet = workbook.getWorksheet('Reference Ranges') || workbook.worksheets[0];
      if (!sheet) {
        errors.push('No worksheets found in the Excel file.');
        return { templates, errors };
      }

      let headerRow: string[] = [];
      sheet.eachRow((row: any, rowNumber: number) => {
        if (rowNumber === 1) {
          headerRow = (row.values as any[]).slice(1).map(v => String(v).trim());
          if (headerRow.length < this.expectedHeaders.length) {
            errors.push(`Header mismatch. Expected columns: ${this.expectedHeaders.join(', ')}`);
          }
          return;
        }

        if (headerRow.length < this.expectedHeaders.length) return; // Skip if invalid headers

        const rowValues = (row.values as any[]).slice(1);
        const getField = (header: string) => {
          const idx = headerRow.indexOf(header);
          const val = idx >= 0 ? rowValues[idx] : undefined;
          return val != null ? String(val).trim() : '';
        };

        const display = getField('observationDisplay');
        if (!display) {
          errors.push(`Row ${rowNumber}: failed to parse (missing observationDisplay)`);
          return;
        }

        const template = this.rowToTemplate(getField, rowNumber, errors, localCodes);
        if (template) {
          templates.push(template);
        }
      });

      return { templates, errors };
    } catch (e) {
      errors.push(`Excel parsing error: ${(e as any)?.message || String(e)}`);
      return { templates, errors };
    }
  }

  async downloadTemplatesAsExcel(templates: ObservationReferenceRangeTemplate[]): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    
    // Add hidden reference sheet for data validation
    const refSheet = workbook.addWorksheet('ReferenceData');
    refSheet.state = 'hidden';
    
    const rangeTypes = Object.keys(this.rangeTypeMap);
    const interpretations = Object.keys(this.interpretationMap);
    const genders = Object.keys(this.genderMap);
    
    refSheet.getColumn(1).values = ['RangeTypes', ...rangeTypes];
    refSheet.getColumn(2).values = ['Interpretations', ...interpretations];
    refSheet.getColumn(3).values = ['Genders', ...genders];

    const dataSheet = workbook.addWorksheet('Reference Ranges');
    dataSheet.addRow(this.expectedHeaders);

    // Apply data validation to columns starting from row 2 up to row 1000
    for (let i = 2; i <= 1000; i++) {
      dataSheet.getCell(`C${i}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`ReferenceData!$A$2:$A$${rangeTypes.length + 1}`]
      };
      
      dataSheet.getCell(`D${i}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`ReferenceData!$B$2:$B$${interpretations.length + 1}`]
      };
      
      dataSheet.getCell(`I${i}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`ReferenceData!$C$2:$C$${genders.length + 1}`]
      };
    }

    for (const template of templates) {
      const code = template.code?.code ?? '';
      const display = template.display ?? '';
      
      const typeCode = template.range?.type?.coding?.[0]?.code ?? 'normal';
      const rangeType = this.reverseMap(this.rangeTypeMap, typeCode);
      
      const intCode = template.range?.interpretation?.coding?.[0]?.code ?? '';
      const interpretation = this.reverseMap(this.interpretationMap, intCode);
      
      const lowValue = template.range?.low?.value ?? '';
      const lowUnit = template.range?.low?.unit || template.range?.low?.code || '';
      const highValue = template.range?.high?.value ?? '';
      const highUnit = template.range?.high?.unit || template.range?.high?.code || '';
      
      const genderRaw = this.extractGenderFromAppliesto(template.range?.appliesTo);
      const genderCode = this.reverseMap(this.genderMap, genderRaw);
      
      const ageLowValue = template.range?.age?.low?.value ?? '';
      const ageLowUnit = template.range?.age?.low?.code || template.range?.age?.low?.unit || '';
      const ageHighValue = template.range?.age?.high?.value ?? '';
      const ageHighUnit = template.range?.age?.high?.code || template.range?.age?.high?.unit || '';
      const text = template.range?.text ?? '';

      dataSheet.addRow([
        code, display, rangeType, interpretation, lowValue, lowUnit, highValue, highUnit,
        genderCode, ageLowValue, ageLowUnit, ageHighValue, ageHighUnit, text
      ]);
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `reference-ranges-export-${new Date().toISOString().split('T')[0]}.xlsx`);
  }

  // ============ Private Helpers ============

    private rowToTemplate(getField: (header: string) => string, rowNumber: number, errors: string[], localCodes: Coding[]): ObservationReferenceRangeTemplate | null {
    const display = getField('observationDisplay');
    if (!display) return null;

    const matchedCode = localCodes.find(c => c.display?.toLowerCase() === display.toLowerCase() || c.code?.toLowerCase() === display.toLowerCase());
    if (!matchedCode) {
      errors.push(`Row ${rowNumber}: Could not find a matching observation code for display '${display}'`);
      return null;
    }

    const coding: Coding = { system: matchedCode.system || 'local', code: matchedCode.code, display: matchedCode.display || display };
    
    const rangeTypeText = getField('rangeType');
    const rangeTypeCode = this.rangeTypeMap[rangeTypeText];
    if (!rangeTypeCode) {
      errors.push(`Row ${rowNumber}: Missing or invalid Meaning (rangeType)`);
      return null;
    }
    
    const interpretationText = getField('interpretation');
    const interpretationCode = this.interpretationMap[interpretationText];
    if (!interpretationCode) {
      errors.push(`Row ${rowNumber}: Missing or invalid Interpretation`);
      return null;
    }
    
    const lowValue = this.parseNumber(getField('lowValue'));
    const lowUnit = getField('lowUnit');
    const highValue = this.parseNumber(getField('highValue'));
    const highUnit = getField('highUnit');
    
    const hasLow = lowValue != null && lowUnit;
    const hasHigh = highValue != null && highUnit;
    if (!hasLow && !hasHigh) {
      errors.push(`Row ${rowNumber}: Must provide at least a Low bound (with unit) or High bound (with unit)`);
      return null;
    }
    
    const genderText = getField('appliesToGender') || 'All';
    const genderCode = this.genderMap[genderText] || 'all';

    const ageLowValue = this.parseNumber(getField('ageLowValue'));
    const ageLowUnit = getField('ageLowUnit') || 'a';
    const ageHighValue = this.parseNumber(getField('ageHighValue'));
    const ageHighUnit = getField('ageHighUnit') || 'a';
    const text = getField('text');

    const range: any = {
      type: { coding: [{ code: rangeTypeCode, display: rangeTypeText }] },
      interpretation: { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation', code: interpretationCode, display: interpretationText }] }
    };

    if (lowValue != null) range.low = { value: lowValue, unit: lowUnit, code: lowUnit, system: this.unitSystem };
    if (highValue != null) range.high = { value: highValue, unit: highUnit, code: highUnit, system: this.unitSystem };

    if (genderCode && genderCode !== 'all') {
      range.appliesTo = [{ coding: [{ code: genderCode, display: genderText }] }];
    }

    if (ageLowValue != null || ageHighValue != null) {
      const ageRange: any = {};
      if (ageLowValue != null) ageRange.low = { value: ageLowValue, unit: ageLowUnit, code: ageLowUnit, system: this.unitSystem };
      if (ageHighValue != null) ageRange.high = { value: ageHighValue, unit: ageHighUnit, code: ageHighUnit, system: this.unitSystem };
      range.age = ageRange;
    }

    if (text) range.text = text;

    return { code: coding, display, range };
  }

  private parseNumber(value: string): number | null {
    const num = Number(value);
    return Number.isNaN(num) || value.trim() === '' ? null : num;
  }

  private extractGenderFromAppliesto(appliesTo?: CodeableConcept[]): string {
    if (!appliesTo || appliesTo.length === 0) return 'all';
    const first = appliesTo[0];
    return (first.coding?.[0]?.code || first.text || 'all').toLowerCase();
  }
}
